import { Controller } from "@hotwired/stimulus"

// The exploded three-floor building behind the home and /building-managers
// heroes (TASK-300; Www::BuildingScene). A port of
// docs/prototypes/hero-animations-prototype.html — the scene, the drag and
// the overlay choreography are the prototype's, restructured, not redesigned.
//
// three.js is not bundled. `srcValue` is the digested URL of
// vendor/javascript/three.module.min.js (r128, served by Propshaft) and the
// controller `import()`s it the first time a scene connects — the argument is
// a variable on purpose, which is what stops esbuild inlining it. One promise
// is shared, so the two scenes on a Turbo session load it once.
//
// Rebuilding the vendored file (three r128 is the version the prototype was
// proven against; the file is the subset this controller touches):
//
//   curl -O https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.module.js
//   printf 'export { %s } from "./three.module.js"' "$(grep -o 'THREE\.[A-Za-z]*' app/javascript/controllers/www_building_scene_controller.js | sort -u | sed 's/THREE\.//' | paste -sd, -)" > entry.js
//   node_modules/.bin/esbuild entry.js --bundle --minify --format=esm --legal-comments=none \
//     --banner:js='/* three.js r128 (MIT) — subset for Www::BuildingScene */' \
//     --outfile=vendor/javascript/three.module.min.js
//
// Modes (modeValue):
//   journey  — the home page's loop: a pin lights, the job card ticks its three
//              checks, submits, and an orange dot flies to the live register
//              card, whose row flips to Compliant with a green flash. ~7.6s.
//   register — /building-managers: a status card cycling door by door every
//              3.4s, the matching pin pulsing.
//
// The markup is the RESTING state (featured door submitted and synced; the
// first door on the status card). With prefers-reduced-motion the controller
// draws the building once, still, and leaves the cards as they are; if three
// fails to load the cards still stand on the scene's background.
let threeModule = null

function loadThree(src) {
  if (!threeModule) threeModule = import(src)
  return threeModule
}

// The scene's own palette: the brand navy for every edge and wall, the brand
// orange for the active pin, a muted orange for idle ones (light theme only —
// the public site has no dark mode).
const PALETTE = {
  slabTop: 0xffffff,
  slabEdge: 0x154265,
  wall: 0x154265,
  partition: 0x9db4c7,
  pin: 0xe05e0b,
  pinActive: 0xe05e0b,
  pinIdle: 0xc98a5e,
  pinGlow: 0x3a1500
}

const SLAB_W = 10
const SLAB_D = 6.4
const FLOOR_GAP = 2.35
const WALL_H = 1.35

const JOURNEY_LENGTH = 7600
const REGISTER_INTERVAL = 3400

export default class extends Controller {
  static values = { mode: String, src: String, floors: Array, labels: Object }
  static targets = [
    "stage",
    // journey
    "phoneCard", "phoneRef", "phonePill", "phoneMeta", "step",
    "regCard", "regRow", "regRowRef", "regRowPill", "regRowWhen", "flyDot",
    // register
    "card", "cardRef", "cardPill", "cardMeta"
  ]

  connect() {
    this.reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.timers = []
    this.alive = true
    this.stageTarget.replaceChildren()

    loadThree(this.srcValue)
      .then((THREE) => {
        if (!this.alive) return
        this.build(THREE)
        this.reduce ? this.rest() : this.start()
      })
      .catch(() => {
        // No WebGL, or no file: the cards rest on the scene's background and
        // the "Drag to rotate" hint goes, since there is nothing to drag.
        this.element.classList.add("www-scene--static")
      })
  }

  disconnect() {
    this.alive = false
    this.clearTimers()
    clearTimeout(this.idleTimer)
    if (this.interval) clearInterval(this.interval)
    if (this.frame) cancelAnimationFrame(this.frame)
    if (this.resizer) this.resizer.disconnect()
    if (this.renderer) {
      this.renderer.dispose()
      this.renderer.domElement.remove()
    }
    this.renderer = null
  }

  // ---- the scene -----------------------------------------------------------

  build(THREE) {
    const container = this.element
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.stageTarget.appendChild(renderer.domElement)

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100)
    camera.position.set(14.5, 10.5, 14.5)
    camera.lookAt(0, 2.6, 0)
    scene.add(new THREE.HemisphereLight(0xffffff, 0x8899aa, 0.95))
    const sun = new THREE.DirectionalLight(0xffffff, 0.55)
    sun.position.set(6, 12, 8)
    scene.add(sun)

    const building = new THREE.Group()
    scene.add(building)

    const slabMaterial = new THREE.MeshLambertMaterial({ color: PALETTE.slabTop })
    const wallMaterial = new THREE.MeshLambertMaterial({ color: PALETTE.wall, transparent: true, opacity: 0.13, depthWrite: false })
    const edgeMaterial = new THREE.LineBasicMaterial({ color: PALETTE.slabEdge, transparent: true, opacity: 0.85 })
    const partitionMaterial = new THREE.LineBasicMaterial({ color: PALETTE.partition, transparent: true, opacity: 0.8 })

    const edges = (geometry, parent, y) => {
      const lines = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgeMaterial)
      lines.position.y = y || 0
      parent.add(lines)
    }
    const partition = (parent, points) => {
      const geometry = new THREE.BufferGeometry().setFromPoints(points.map(([x, z]) => new THREE.Vector3(x, 0.11, z)))
      parent.add(new THREE.Line(geometry, partitionMaterial))
    }

    const pins = []
    const floors = this.floorsValue
    floors.forEach((floor, floorIndex) => {
      const group = new THREE.Group()
      group.position.y = floorIndex * FLOOR_GAP
      building.add(group)

      const slab = new THREE.BoxGeometry(SLAB_W, 0.18, SLAB_D)
      group.add(new THREE.Mesh(slab, slabMaterial))
      edges(slab, group, 0)

      const walls = new THREE.BoxGeometry(SLAB_W, WALL_H, SLAB_D)
      const wall = new THREE.Mesh(walls, wallMaterial)
      wall.position.y = WALL_H / 2 + 0.09
      group.add(wall)
      edges(walls, group, WALL_H / 2 + 0.09)

      partition(group, [[-SLAB_W / 2 + 1.4, 0.9], [1.6, 0.9]])
      partition(group, [[1.6, -SLAB_D / 2], [1.6, 0.9]])
      partition(group, [[-1.2, 0.9], [-1.2, SLAB_D / 2]])
      partition(group, [[-SLAB_W / 2, -0.8], [-0.4, -0.8]])
      partition(group, [[3.4, 0.9], [3.4, SLAB_D / 2]])

      floor.doors.forEach((door) => {
        const material = new THREE.MeshLambertMaterial({ color: PALETTE.pinIdle, emissive: 0x000000 })
        const pin = new THREE.Group()
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.55, 10), material)
        post.position.y = 0.37
        const head = new THREE.Mesh(new THREE.SphereGeometry(0.17, 18, 14), material)
        head.position.y = 0.72
        pin.add(post, head)
        pin.position.set(door.x * 0.94, 0.1, door.z * 0.94)
        group.add(pin)

        const ring = new THREE.Mesh(
          new THREE.RingGeometry(0.22, 0.27, 36),
          new THREE.MeshBasicMaterial({ color: PALETTE.pin, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false })
        )
        ring.rotation.x = -Math.PI / 2
        ring.position.set(pin.position.x, 0.12, pin.position.z)
        ring.visible = false
        group.add(ring)

        pins.push({ head, material, ring, door, floor: floor.name })
      })
    })
    building.position.y = -((floors.length - 1) * FLOOR_GAP) / 2 + 2.3

    this.renderer = renderer
    this.camera = camera
    this.scene = scene
    this.building = building
    this.pins = pins
    this.active = -1
    this.projected = new THREE.Vector3()

    // Drag to rotate; auto-spin resumes 2.5s after the pointer lets go.
    this.targetRotation = 0.6
    this.rotation = 0.6
    this.autoSpin = !this.reduce
    const canvas = renderer.domElement
    canvas.style.cursor = "grab"
    let dragging = false
    let lastX = 0
    canvas.addEventListener("pointerdown", (event) => {
      dragging = true
      lastX = event.clientX
      this.autoSpin = false
      canvas.style.cursor = "grabbing"
      canvas.setPointerCapture(event.pointerId)
      clearTimeout(this.idleTimer)
    })
    canvas.addEventListener("pointermove", (event) => {
      if (!dragging) return
      this.targetRotation += (event.clientX - lastX) * 0.008
      lastX = event.clientX
    })
    const release = () => {
      if (!dragging) return
      dragging = false
      canvas.style.cursor = "grab"
      this.idleTimer = setTimeout(() => { this.autoSpin = !this.reduce }, 2500)
    }
    canvas.addEventListener("pointerup", release)
    canvas.addEventListener("pointercancel", release)

    const resize = () => {
      const width = container.clientWidth
      const height = container.clientHeight
      if (!width || !height) return
      renderer.setSize(width, height, false)
      camera.aspect = width / height
      camera.updateProjectionMatrix()
    }
    this.resizer = new ResizeObserver(resize)
    this.resizer.observe(container)
    resize()

    const started = performance.now()
    const tick = (now) => {
      if (!this.alive) return
      this.frame = requestAnimationFrame(tick)
      const t = (now - started) / 1000
      if (this.autoSpin) this.targetRotation += 0.0028
      this.rotation += (this.targetRotation - this.rotation) * 0.08
      building.rotation.y = this.rotation
      if (!this.reduce && this.active >= 0) {
        const pin = pins[this.active]
        const phase = (t % 1.6) / 1.6
        pin.ring.scale.setScalar(1 + phase * 2.1)
        pin.ring.material.opacity = 0.55 * (1 - phase)
        pin.head.scale.setScalar(1 + 0.12 * Math.sin(t * 5))
      }
      renderer.render(scene, camera)
    }
    this.frame = requestAnimationFrame(tick)
  }

  setActive(index) {
    if (this.active >= 0) {
      const previous = this.pins[this.active]
      previous.material.color.setHex(PALETTE.pinIdle)
      previous.material.emissive.setHex(0)
      previous.ring.visible = false
      previous.head.scale.setScalar(1)
    }
    this.active = index
    if (index < 0) return
    const pin = this.pins[index]
    pin.material.color.setHex(PALETTE.pinActive)
    pin.material.emissive.setHex(PALETTE.pinGlow)
    pin.ring.visible = !this.reduce
  }

  // Where a pin's head is on the overlay, in pixels from the scene's corner.
  pinScreenPosition(index) {
    const v = this.projected
    this.pins[index].head.getWorldPosition(v)
    v.project(this.camera)
    return {
      x: (v.x + 1) / 2 * this.element.clientWidth,
      y: (1 - (v.y + 1) / 2) * this.element.clientHeight
    }
  }

  // ---- choreography --------------------------------------------------------

  rest() {
    const index = this.modeValue === "journey" ? this.labelsValue.featured : this.labelsValue.first
    this.setActive(index)
  }

  start() {
    if (this.modeValue === "journey") {
      this.journeyStep = 0
      this.journey()
    } else {
      this.registerIndex = this.labelsValue.first
      this.showDoor(this.registerIndex)
      this.interval = setInterval(() => {
        this.registerIndex = (this.registerIndex + 1) % this.pins.length
        this.showDoor(this.registerIndex)
      }, REGISTER_INTERVAL)
    }
  }

  at(ms, fn) {
    this.timers.push(setTimeout(fn, ms))
  }

  clearTimers() {
    this.timers.forEach(clearTimeout)
    this.timers = []
  }

  journey() {
    this.clearTimers()
    const route = this.labelsValue.journey
    const index = route[this.journeyStep % route.length]
    this.journeyStep += 1
    const pin = this.pins[index]
    const labels = this.labelsValue

    // Rewind the cards for this door.
    this.phoneRefTarget.textContent = pin.door.ref
    this.setPill(this.phonePillTarget, "due", labels.scanning)
    this.phoneMetaTarget.replaceChildren(this.bold(`${pin.floor} · ${pin.door.loc}`))
    this.stepTargets.forEach((step) => step.classList.remove("is-on"))
    this.phoneCardTarget.classList.add("www-art-card--swap")
    this.at(250, () => this.phoneCardTarget.classList.remove("www-art-card--swap"))
    this.regRowRefTarget.textContent = pin.door.ref
    this.setPill(this.regRowPillTarget, "due", labels.due_soon)
    this.regRowWhenTarget.textContent = pin.door.last
    this.regRowTarget.classList.remove("is-flash")
    this.setActive(index)

    this.at(1400, () => {
      this.setPill(this.phonePillTarget, "due", labels.inspecting)
      this.stepTargets[0].classList.add("is-on")
    })
    this.at(2200, () => this.stepTargets[1].classList.add("is-on"))
    this.at(3000, () => this.stepTargets[2].classList.add("is-on"))
    this.at(3900, () => {
      this.setPill(this.phonePillTarget, "ok", labels.submitted)
      this.flyDot(index)
    })
    this.at(4750, () => {
      this.flyDotTarget.style.opacity = "0"
      this.setPill(this.regRowPillTarget, "ok", labels.compliant)
      this.regRowWhenTarget.textContent = labels.just_now
      this.flash(this.regRowTarget)
    })
    this.at(JOURNEY_LENGTH, () => this.journey())
  }

  // The orange dot: from the lit pin to the foot of the register card.
  flyDot(index) {
    const dot = this.flyDotTarget
    const from = this.pinScreenPosition(index)
    const card = this.regCardTarget.getBoundingClientRect()
    const scene = this.element.getBoundingClientRect()
    dot.style.transition = "none"
    dot.style.left = `${from.x - 6}px`
    dot.style.top = `${from.y - 6}px`
    dot.style.opacity = "1"
    void dot.offsetWidth
    dot.style.transition = ""
    dot.style.left = `${card.left - scene.left + card.width * 0.5 - 6}px`
    dot.style.top = `${card.top - scene.top + card.height - 10}px`
  }

  showDoor(index) {
    this.setActive(index)
    const pin = this.pins[index]
    const card = this.cardTarget
    card.classList.add("www-art-card--swap")
    this.clearTimers()
    this.at(250, () => {
      this.cardRefTarget.textContent = pin.door.ref
      this.setPill(this.cardPillTarget, pin.door.st, this.labelsValue.status[pin.door.st])
      this.cardMetaTarget.replaceChildren(
        this.bold(`${pin.floor} · ${pin.door.loc}`),
        document.createElement("br"),
        pin.door.meta
      )
      card.classList.remove("www-art-card--swap")
    })
  }

  // ---- DOM helpers ---------------------------------------------------------

  setPill(pill, status, text) {
    pill.className = `www-art-pill www-art-pill--${status}`
    pill.textContent = text
  }

  bold(text) {
    const b = document.createElement("b")
    b.textContent = text
    return b
  }

  flash(row) {
    row.classList.remove("is-flash")
    void row.offsetWidth
    row.classList.add("is-flash")
  }
}
